import React, { useEffect, useState, memo } from "react";
import { Button, Dropdown, Form, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import { auth } from "../../../../firebase";
import {
  API_BASE_URL,
  USER_GET_SUB_MODULE_DATA,
  ADMIN_REORDERING_SUB_MODULES,
} from "../../../../config";
import SubModule from "./SubModule";

toast.configure();

function Module(props) {
  // CONSTANTS
  const {
    module_data,
    module_idx,
    className,
    setSubModuleOpen,
    selectedModuleId,
    setSelectedModuleId,
    setSelectedSubModuleType,
    setFetchingSubModuleData,
    selectedSubModuleData,
    setSelectedSubModuleData,
    handleCloseSubModule,
    handleEditSubModule,
    handleRemoveSubModule,
    setShowAddSubModuleSection,
    setShowAddAssignmentModule,
    setShowAddForm,
    setShowAddQuizModule,
    setShowAddScheduleModule,
    setShowAddResource,
    setNewSubModuleDetails,
  } = props;

  // STATES
  const [showEditSubModuleModal, setShowEditSubModuleModal] = useState(false);

  // FUNCTIONS
  const handleOpenSubModule = async (data) => {
    const { id, parent_id, type } = data;

    setSelectedModuleId(parent_id);
    setSelectedSubModuleType(type);
    setSubModuleOpen(true);

    if (!id || !data.hasOwnProperty("id")) {
      toast.dark("Please refresh and try again!", {
        position: toast.POSITION.TOP_CENTER,
        hideProgressBar: false,
        autoClose: 3000,
      });
    }

    try {
      setFetchingSubModuleData(true);

      const response = await axios.get(
        `${API_BASE_URL}${USER_GET_SUB_MODULE_DATA}?user_id=${auth?.currentUser?.uid}&sub_module_id=${id}`
      );

      if (response.status === 200) {
        setSelectedSubModuleData(response.data.data);
        setFetchingSubModuleData(false);
      } else {
        setFetchingSubModuleData(false);
      }
    } catch (error) {
      setFetchingSubModuleData(false);
    }
  };

  const grid = 8;

  const getListStyle = (isDraggingOver) => ({
    // background: isDraggingOver ? "white" : "white",
    // display: "flex",
    padding: grid,
    overflow: "auto",
  });

  const getItemStyle = (isDragging, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: "none",
    // change background colour if dragging
    background: isDragging ? "black" : "",
    // styles we need to apply on draggables
    ...draggableStyle,
  });

  const handleReorderingSubModules = async (e) => {
    const { destination, source } = e;
    const to = destination.index;
    const from = source.index;
    const product_id = destination?.droppableId?.split("~")[0];
    const module_id = destination?.droppableId?.split("~")[1];

    if (from != to) {
      try {
        const response = await axios.post(
          `${API_BASE_URL}${ADMIN_REORDERING_SUB_MODULES}`,
          {
            from,
            to,
            product_id,
            module_id,
            user_id: auth.currentUser.uid,
          }
        );

        if (response.status === 200) {
          toast.success("Reordering of items completed", {
            position: toast.POSITION.TOP_CENTER,
            hideProgressBar: false,
            autoClose: 3000,
          });
        }
      } catch (error) {
        console.log(`[Error] Reordering items: ${error.message}`);
        toast.danger(`Error reordering items: ${error.message}`, {
          position: toast.POSITION.TOP_CENTER,
          hideProgressBar: false,
          autoClose: 3000,
        });
      }
    }
  };

  const handleShowEditSubModuleModal = (data) => {
    setShowEditSubModuleModal(true);
    setSelectedSubModuleData(data);
  };

  const handleHideEditSubModuleModal = () => {
    setShowEditSubModuleModal(false);
    setSelectedSubModuleData(null);
  };

  useEffect(() => {
    if (module_data?.id != selectedModuleId) {
      handleCloseSubModule();
    }
  }, [module_data]);

  return (
    <React.Fragment>
      <div key={module_idx}>
        <div className="border-bottom p-3 border-dark">
          <h4 className="text-white mb-0">{module_data.name}</h4>
        </div>
        <div className="p-3">
          <div className={className}>
            <div>
              <DragDropContext onDragEnd={handleReorderingSubModules}>
                <Droppable
                  droppableId={`${module_data?.parent_id}~${module_data?.id}`}
                  direction="vertical"
                >
                  {(provided, snapshot) => (
                    <div
                      className={
                        snapshot.isDraggingOver ? "border border-primary" : ""
                      }
                      ref={provided.innerRef}
                      style={getListStyle(snapshot.isDraggingOver)}
                      {...provided.droppableProps}
                    >
                      {module_data?.sub_modules?.length > 0 ? (
                        module_data?.sub_modules
                          ?.sort((a, b) => a.order_index - b.order_index)
                          ?.map((item, key) => (
                            <Draggable
                              key={`${key}`}
                              draggableId={`${key}`}
                              index={item.order_index}
                            >
                              {(provided, snapshot) => (
                                <div
                                  className={
                                    snapshot.isDragging
                                      ? "rounded border border-white"
                                      : "rounded"
                                  }
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={getItemStyle(
                                    snapshot.isDragging,
                                    provided.draggableProps.style
                                  )}
                                >
                                  <SubModule
                                    idx={key}
                                    data={item}
                                    selectedSubModuleData={
                                      selectedSubModuleData
                                    }
                                    handleOpenSubModule={handleOpenSubModule}
                                    handleShowEditSubModuleModal={
                                      handleShowEditSubModuleModal
                                    }
                                    handleRemoveSubModule={
                                      handleRemoveSubModule
                                    }
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))
                      ) : (
                        <p className="mb-3 text-center">
                          Click the button to add content in the module
                        </p>
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              <Dropdown className="pointer no-dropdown-caret ml-auto">
                <Dropdown.Toggle
                  as="div"
                  id="dropdown-basic"
                  className="btn btn-secondary btn-sm text-light mr-2 w-100"
                >
                  {" "}
                  ADD CONTENT
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {/* Add Live Session */}
                  <Dropdown.Item
                    className="border-bottom"
                    onClick={() => {
                      setShowAddScheduleModule(true);
                      setShowAddAssignmentModule(false);
                      setShowAddForm(false);
                      setShowAddQuizModule(false);
                      setShowAddResource(false);
                      setShowAddSubModuleSection(true);
                      handleCloseSubModule();
                    }}
                  >
                    <div className="d-flex">
                      <img
                        src="data:image/png;base64,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"
                        width="30px"
                        height="30px"
                        className="mr-2"
                      />
                      <div>
                        <span className="font-weight-bold text-uppercase  small">
                          Add Live Session
                        </span>
                        <p className="small my-0 ">
                          Create an online session with your video meeting link.{" "}
                          <br />
                          You can also add an offline session.
                        </p>
                      </div>
                    </div>
                  </Dropdown.Item>

                  {/* Add Assignment */}
                  <Dropdown.Item
                    className="border-bottom"
                    onClick={() => {
                      setShowAddScheduleModule(false);
                      setShowAddAssignmentModule(true);
                      setShowAddForm(false);
                      setShowAddQuizModule(false);
                      setShowAddResource(false);
                      setShowAddSubModuleSection(true);
                      handleCloseSubModule();
                    }}
                  >
                    <div className="d-flex">
                      <img
                        src="data:image/png;base64,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"
                        width="30px"
                        height="30px"
                        className="mr-2"
                      />
                      <div>
                        <span className="font-weight-bold text-uppercase  small">
                          Add Assignment
                        </span>
                        <p className="small my-0 ">
                          Create an assignment to assess your participants.{" "}
                          <br />
                          Participants can share their replies as text and/or
                          upload a document.
                        </p>
                      </div>
                    </div>
                  </Dropdown.Item>

                  {/* Add Quiz */}
                  <Dropdown.Item
                    className="border-bottom"
                    onClick={() => {
                      setShowAddScheduleModule(false);
                      setShowAddAssignmentModule(false);
                      setShowAddForm(false);
                      setShowAddQuizModule(true);
                      setShowAddResource(false);
                      setShowAddSubModuleSection(true);
                      handleCloseSubModule();
                    }}
                  >
                    <div className="d-flex">
                      <img
                        src="data:image/png;base64,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"
                        width="30px"
                        height="30px"
                        className="mr-2"
                      />
                      <div>
                        <span className="font-weight-bold text-uppercase  small">
                          Add Quiz
                        </span>
                        <p className="small my-0 ">
                          Create a multiple choice with single or multiple
                          answers. <br />
                          You can use quizzes to grade your participants as
                          well.
                        </p>
                      </div>
                    </div>
                  </Dropdown.Item>

                  {/* Add Recorded Lecture */}
                  <Dropdown.Item
                    className="border-bottom"
                    onClick={() => {
                      setNewSubModuleDetails((prev) => {
                        return {
                          ...prev,
                          order_index:
                            module_data?.sub_modules?.length === 0
                              ? 1
                              : module_data?.sub_modules?.length + 1,
                          type: "lecture",
                          module_id: module_data?.id,
                        };
                      });
                      setShowAddScheduleModule(false);
                      setShowAddAssignmentModule(false);
                      setShowAddForm(false);
                      setShowAddQuizModule(false);
                      setShowAddResource(true);
                      setShowAddSubModuleSection(true);
                      handleCloseSubModule();
                    }}
                  >
                    <div className="d-flex">
                      <img
                        src="data:image/png;base64,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"
                        width="30px"
                        height="30px"
                        className="mr-2"
                      />
                      <div>
                        <span className="font-weight-bold text-uppercase  small">
                          Add Recorded Lecture
                        </span>
                        <p className="small my-0 ">
                          Select a video file (mp4 only) <br />
                        </p>
                      </div>
                    </div>
                  </Dropdown.Item>

                  {/* Add File */}
                  <Dropdown.Item
                    className="border-bottom"
                    onClick={() => {
                      setNewSubModuleDetails((prev) => {
                        return {
                          ...prev,
                          order_index:
                            module_data?.sub_modules?.length === 0
                              ? 1
                              : module_data?.sub_modules?.length + 1,
                          type: "file",
                          module_id: module_data?.id,
                        };
                      });
                      setShowAddScheduleModule(false);
                      setShowAddAssignmentModule(false);
                      setShowAddForm(false);
                      setShowAddQuizModule(false);
                      setShowAddResource(true);
                      setShowAddSubModuleSection(true);
                      handleCloseSubModule();
                    }}
                  >
                    <div className="d-flex">
                      <img
                        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEgAAABICAYAAABV7bNHAAAKv0lEQVR4Ae2cBYwjOxKGv+pkZnn38dIxMzMzMzMzMzMzMzMzM5PomG/e6paZdyBJt+sSWaU6lea272XTSu40JVmudvsfl/+uKtudSViSyZUlWZIlWRJpuP+kio5ywsL/t2jd5Bu4/z/nMTrMBKWmXbxMFGkar63UkKFDkLBoW4ET0yLXRQ1mHB6iQCLXFU5SqsHUE1FDTmtQLvL231y2teliD5bp5TeRVnuzKquJokNSNAK8CCe0Kndqd+EH1a6ZD2557JX/BFS51JN0XggSL7TW3fY+K9c/9N2vYfnqhwCFqiIiWG0Sr+vaGsYnFk58YO/7H/mMo1//xFzwKP3vCaohZ+0t775q/WM+/kVa7RsA0bCT6tbf9LHgU/mTPW+/752Pffuzs7Uk1RDkuuea6Yt99vgbixWrH+ZGRnQYKkzE9THiu7Pv+/td1zwZ6DpJTkrUW9R7TwFMXej1P7t8cc7F3oaqeHoIRmm4NlWjPkZ8a9mV1l7t5l878p0P7Ae0bpFqs7hIIKjd2ny5B4mq5RyExeNfk4KAENrN9rHjUzGYC/CM4EG47lJQI7ZyyfTKG6PkgZMOiunelu9D8shWJfdRmBS8TK24cY6e+vm3a1YxsSLa2qxJcXH39ZxgutcCqPeZCLzQ3hyiRGMWO68h1lJY5VA3xti89lmJ51+mxznLvJMIqOJhAGi4Z0k0i1rb0Pg988LL/zLFLw4UwH8kdpV5UDgNaJ0HRRGrNWk2FAXF8wDZxQfkrF8ed3ZGoM9KnF18Yo4TOTX8hhXwvL4tt/nxsrjU4/aHVbo2xOq9KHArMRn2ybEnjYlPQOyerzyK5PuoLYwjww9sUXWboxeBuOJF6wiqSdRGivk9kEDtcWTLKcTaxLKi6TYhUFx3zGjxSZ0cAAn21yfomhwUdBsQjZsM6yRmpOcMxJ+N6bhXoI4ZNR51E0Wi/YvPcyQ5CNxtbXTzqIICRbPPiumCivoEfK5oxlh6HikexfOOxpw1ihwURYku6+6O5YOs48cCUKvdQNTgvg6PHB8dJtofZRQEqWrc2gOKRj/11QYVbzOoiNf4Tjfi2bkgfGlvm3PnYE1LucmZFTc4U+vxId+YuP2jIUgWJwkkxLKnB/ElIWwHMlAofGvgxwWbiOP5+xy8e+sUCwm6lXKoA+850eLcWeUhF6xq8GZrQjxnuf0EGcEqhqb4BstJQCTGdzasAOJmzu7h7fH+1nnhvVvbA3LoVUovQan0a+Wre0C14GEX0kXwLpbkNW67kYZCzFzWd7t2jah5kZgRnizFdPMrsh4Om4bf1YF3b28zn6BM0EuZmEHJ18pndoIiPPxCjo/jk5wLM9jtbyYH+eDZIN9riLu5GezG+sS/uLfNvo5woRXKXTaUrGv700dgf0d517YpjveUnpNDlTBysidVyoe3AsAjLux4fHzbzIZzidvfCEHxACh2kUz3CeOrKNsX4F1bW/Q0t/3mKMycaPPsi1esaWXc4V4m53AXShXK7DEYMU4OGEnvnAEUHnnRIis+PiQAPBeC299AiHlMh4AR9Sdmew4RN+ZgFz643cnpZs9gV1d5/YzwgkvBbKm8c2ub/R0yIZWS65RJ0tDuIcfr/6YIyiP6JMXxMUvNVre/CQ8SjH9Usy5h9woInnznKvjAjhYnKgHyatRJ0KkS3QS/PwJvnlGqVps9HaGXkidk8xjNOCPKyKpUMKJe8ielEOHhFxWfunplrCk0F2KanHeFYIg/KYrsURXCB7YX7O8K4Am3UyndalDnyf5jvsVUgXmEeUqefPSaQbtm3Yis8jXP+G3Z96JpG9/tFU8Pbn8zSdr3PGq8uBEi7sqI8IntBVvnc1ulmZhOUrplvyQoK2XT6oLpQvJks8fkyatSKbjXuEcZiUZe11e3HGLkWhR8pYz2N0KQorh7Ck6Ib5dz+1f2Fvz+uJ9xO3kidAbk5Gs2ri5Y0S7o/ru3JPcka88e5aR0KyMykZN5xqridomA+qYi33P7acqDXHzvYcxY3lHgJweNPAupQYFuytfrVxasaheZADXPUCyczHOcNO9Tagg7td2Gjw8aVnUlnlObICh8iuC7a8FDrMAH71X21MmJuYKzlhesnS7oJqWbScFJMGKMLGsPHqVQ+h7JRfL4Cp6DjJxgfwM76XAaDqdqfx2hXGYN/O5o9pYcXvSLcPoy+sXDyjwlT1icGM89waM8iRve5FabWphRQngfRPOneT8dh/dB6hmQQnK8P+JC1kfc48z9VT0c1D0v617jbQEPsEjfiI85yDs3m4M8ns2KrO+dgw2rBClwEuyslhvJMGtzYr0vI8HvOaGgNfY3l4NAE7lWH/mZ3+vx6ptOsXGNoGaweZG9ZK/5bwwk9zkV/J7jyrN+0EOT97PH6fY3QRAgajFsBrrn/uifiWu+v+N9xfvaPHC3x/t4DYwWn5Ljg/01MqL3Qark2iyN9yToE4Nv6KgR3qdI2KLmPhCNyP0nDw/1UpynJK0KKv76IGn2LD/7oCm3o1Yrgkwavtn3QQh5cARPejEfBAyThR8tQXF0FcDc1V+phrwQYn6y8A0QZK5pBmXdFuC4PwKXfG+i8M0k6ciXWAIUjRsx33/IZOIb20nbgyCBkrAXHze6cMGrbjPNxtXx83P3bHxGQbeu3rZ7sOH7Vpcfnpvwm3Dji/bHuVV/nLWyKH7PscQzv9njh1uS5yoJ9jf6Tlr9oxubuarwslu0OHtFoqzA3Dm6sp/BEjEYYp9zVsLLbt7iujMJ1N8pv/wWbc5emSjLxfFnrYSX37LNdd7RdQIdj6qOjCBd/MpP7uIxTVVV/VKgEP5FxcWMJKZP/1wLx0NVhY+8gbIcjCMnxZdlAvUxHO/2B9FT9iBZ7H2Q2uoAz/tW4sW3SGxYXfh4auB4ynadkFhNHxw4X/AdLERs/P44FS+6OYMz32L4Po4+Tg3n5DX8Pkg1MQusiu+DzLYfb1Fu9C5AqnDvZGctm2DgJ+IxfB7nxu8GEa3HQ8QbObOAjmInrWRJWnV2oWBFk+bBfOeaxa+t9r6Tgq96u2q+0FLrQQqID4kyf/wnrFx28fg5OJjuifxkMhH4+aM/IYMTLjpMiBmo6m378cenLnnXBwMFyd1f0RA2Fvsh3v0eY8UXknpbfvxxoPI5DhVi7pBAOvCZB/5FZw9+DHdbYqxr+MpaSsnvWz1mvJ7Y/7EDX3jgX4AUvvFzSqf5BJRHPn77F2l3/pcAMQ9YDX5PVDDdc4MwLnzqzP7yyMfv+CKgNBrrpFXzT40Cfl3O7tXezLe+Pn2pO54u7VWXF+z04/9linpOsBrzcu8T+jWMR1Ka3/+xox+79RM7+/4wC3QtMnJxL6onKBAT71Wz+6rZX735x9PrNn1HVqwvpZheTVEsQ5kGHypODCDqse9o8TpL2d3WJ+Yrvb9+/tkHPnqrT/dtnyeTUwZyTHSYfZDiabFnbYe+/vg/AH/B/0aRy/jFJm+Oj5deLoGcIZd5F0/WhAFKJyZ63FhEg57w0stz8OQcSx1BSpyYgxKZhCr2N3JCGadoKMlrJ6cm92idB0XCNJBk10YQ3nf8BIU6RbLqcs9wXwt3PRYiSeMXJ8evY/H2iB0mSUfRmi+ijT0H1ayNrteIDOFZUtM2DqK0pl1r2kx0dD9uUk/kGKRm4sP/uMnSz+Ms/cDSkOT9Cz4qNGlYzhzEAAAAAElFTkSuQmCC"
                        width="30px"
                        height="30px"
                        className="mr-2"
                      />
                      <div>
                        <span className="font-weight-bold text-uppercase  small">
                          Add File
                        </span>
                        <p className="small my-0 ">
                          Share a reading material. <br />
                          Upload a document (all image and document extensions
                          allowed)
                        </p>
                      </div>
                    </div>
                  </Dropdown.Item>

                  {/* Add Form */}
                  <Dropdown.Item
                    className=""
                    onClick={() => {
                      setNewSubModuleDetails((prev) => {
                        return {
                          ...prev,
                          order_index:
                            module_data?.sub_modules?.length === 0
                              ? 1
                              : module_data?.sub_modules?.length + 1,
                          type: "file",
                          module_id: module_data?.id,
                        };
                      });
                      setShowAddScheduleModule(false);
                      setShowAddAssignmentModule(false);
                      setShowAddForm(true);
                      setShowAddQuizModule(false);
                      setShowAddResource(false);
                      setShowAddSubModuleSection(true);
                      handleCloseSubModule();
                    }}
                  >
                    <div className="d-flex">
                      <img
                        src="data:image/png;base64,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"
                        width="30px"
                        height="30px"
                        className="mr-2"
                      />
                      <div>
                        <span className="font-weight-bold text-uppercase  small">
                          Add Form
                        </span>
                        <p className="small my-0 ">
                          Share a reading material. <br />
                          Upload a document (all image and document extensions
                          allowed)
                        </p>
                      </div>
                    </div>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>
      </div>

      {/* Modal: Edit/ Update sub-module details */}
      <Modal
        show={showEditSubModuleModal}
        onHide={handleHideEditSubModuleModal}
      >
        <Modal.Header closeButton>
          <h4 className="mb-0">Update item details</h4>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleEditSubModule(
                selectedSubModuleData,
                handleHideEditSubModuleModal,
                handleHideEditSubModuleModal
              );
            }}
          >
            <Form.Group>
              <Form.Label className="text-uppercase small">
                <b>Title</b>
              </Form.Label>
              <Form.Control
                name="resource_title"
                id="resource_title"
                placeholder="Enter Resource title"
                onChange={(e) =>
                  setSelectedSubModuleData((prev) => {
                    let newState = { ...prev };
                    newState["title"] = e.target.value;
                    return newState;
                  })
                }
                value={selectedSubModuleData?.title}
                type="text"
                // disabled={uploading}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label className="small">
                <b className="text-uppercase">Description</b>
                <p className="mb-0">
                  Recommdended: Min. 50 words (Max. 1500 characters)
                </p>
              </Form.Label>
              <CKEditor
                id="editor"
                editor={ClassicEditor}
                config={{
                  toolbar: {
                    items: [
                      "heading",
                      "|",
                      "bold",
                      "italic",
                      "|",
                      "link",
                      "|",
                      "bulletedList",
                      "numberedList",
                      // "-", // break point
                      // "insertTable",
                      // "|",
                      // "outdent",
                      // "indent",
                      // "|",
                      // "uploadImage",
                      "blockQuote",
                      "|",
                      "undo",
                      "redo",
                    ],
                    shouldNotGroupWhenFull: true,
                  },
                }}
                className="mb-3"
                data={selectedSubModuleData?.description ?? ""}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  setSelectedSubModuleData((prev) => {
                    let newState = { ...prev };
                    newState["description"] = data;
                    return newState;
                  });
                }}
                style={{ height: 400 }}
              />
            </Form.Group>

            <Button type="submit" className="rounded-lg text-uppercase">
              Submit
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
}

export default memo(Module);
